import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, User, Phone, Mail, Camera, Star, Car, Edit2, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const PassengerProfile: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, updateProfile } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(currentUser?.name || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProfile({ name, email });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/passenger/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">الملف الشخصي</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-col items-center">
            {/* Photo */}
            <div className="relative mb-4">
              <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center">
                {currentUser?.photoURL ? (
                  <img 
                    src={currentUser.photoURL} 
                    alt={currentUser.name} 
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-primary-600" />
                )}
              </div>
              <button className="absolute bottom-0 left-0 w-8 h-8 bg-primary-500 text-white rounded-full flex items-center justify-center shadow-lg">
                <Camera className="w-4 h-4" />
              </button>
            </div>

            {/* Name */}
            {isEditing ? (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="text-xl font-bold text-center border-b-2 border-primary-500 focus:outline-none mb-2"
              />
            ) : (
              <h2 className="text-xl font-bold mb-1">{currentUser?.name}</h2>
            )}
            
            <p className="text-gray-500" dir="ltr">{currentUser?.phone}</p>

            {/* Rating */}
            <div className="flex items-center gap-2 mt-3">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="font-bold">{currentUser?.rating || '5.0'}</span>
              <span className="text-gray-500">({currentUser?.totalRides || 0} رحلة)</span>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">المعلومات الشخصية</h3>
            <button
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              className="text-primary-500 flex items-center gap-1"
            >
              {isEditing ? (
                <>{isSaving ? 'جاري الحفظ...' : <><CheckCircle className="w-4 h-4" /> حفظ</>}</>
              ) : (
                <><Edit2 className="w-4 h-4" /> تعديل</>
              )}
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                <Phone className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">رقم الهاتف</p>
                <p className="font-medium" dir="ltr">{currentUser?.phone}</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">البريد الإلكتروني</p>
                {isEditing ? (
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1 focus:outline-none focus:border-primary-500"
                  />
                ) : (
                  <p className="font-medium">{currentUser?.email || 'غير محدد'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold text-lg mb-4">إحصائياتي</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-primary-50 rounded-xl p-4 text-center">
              <Car className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-primary-600">{currentUser?.totalRides || 0}</p>
              <p className="text-sm text-gray-600">رحلة منجزة</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-yellow-600">{currentUser?.rating || '5.0'}</p>
              <p className="text-sm text-gray-600">تقييمي</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PassengerProfile;
